import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { format } from 'date-fns';
import TaskChat from './TaskChat';
import { taskService } from '../../../services/taskService';

const MultiMemberTaskCard = ({
  task,
  familyMembers,
  onToggleMemberCompletion,
  onSendMessage,
  onUpdateTask,
  onDeleteTask
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [realtimeChannel, setRealtimeChannel] = useState(null);
  const [localCompletionStatus, setLocalCompletionStatus] = useState(task?.completionStatus || {});

  // Real-time subscription for assignment updates
  useEffect(() => {
    if (!task?.id) return;

    const channel = taskService?.subscribeToTaskAssignments(
      task?.id,
      (payload) => {
        if (payload?.eventType === 'UPDATE') {
          setLocalCompletionStatus(prev => ({
            ...prev,
            [payload?.new?.member_id]: {
              completed: payload?.new?.completed,
              completedAt: payload?.new?.completed_at
            }
          }));
        }
      }
    );

    setRealtimeChannel(channel);

    return () => {
      if (channel) {
        taskService?.unsubscribe(channel);
      }
    };
  }, [task?.id]);

  // Update local state when task prop changes
  useEffect(() => {
    setLocalCompletionStatus(task?.completionStatus || {});
  }, [task?.completionStatus]);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#EF4444';
      case 'medium': return '#F59E0B';
      case 'low': return '#10B981';
      default: return '#6B7280';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return 'alert-circle';
      case 'medium': return 'alert-triangle';
      case 'low': return 'info';
      default: return 'minus';
    }
  };

  const isOverdue = new Date(task?.dueDate) < new Date() && !task?.isComplete;

  const assignedMembersData = familyMembers?.filter(member => 
    task?.assignedMembers?.includes(member?.id)
  );

  // Generate gradient for multi-member visualization
  const generateMemberGradient = () => {
    if (assignedMembersData?.length === familyMembers?.length) {
      // All members - glittery rainbow
      return 'linear-gradient(135deg, #EF4444 0%, #F97316 14%, #F59E0B 28%, #84CC16 42%, #10B981 56%, #3B82F6 70%, #8B5CF6 84%, #EC4899 100%)';
    } else if (assignedMembersData?.length > 1) {
      // Multiple members - gradient blend
      const colors = assignedMembersData?.map(m => m?.color)?.join(', ');
      return `linear-gradient(135deg, ${colors})`;
    } else if (assignedMembersData?.length === 1) {
      return assignedMembersData?.[0]?.color;
    }
    return '#6B7280';
  };

  const handleDelete = () => {
    onDeleteTask(task?.id);
    setShowDeleteConfirm(false);
  };

  // Use localCompletionStatus instead of task.completionStatus in the render
  const completedCount = task?.assignedMembers?.filter(
    memberId => localCompletionStatus?.[memberId]?.completed
  )?.length || 0;

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-warm-md transition-smooth">
      {/* Task Header */}
      <div className="p-4 md:p-6">
        <div className="flex items-start gap-4">
          {/* Progress Circle */}
          <div className="flex-shrink-0 relative w-16 h-16">
            <svg className="w-16 h-16 transform -rotate-90">
              <circle
                cx="32"
                cy="32"
                r="28"
                stroke="#E5E7EB"
                strokeWidth="6"
                fill="none"
              />
              <circle
                cx="32"
                cy="32"
                r="28"
                stroke={task?.isComplete ? '#10B981' : '#3B82F6'}
                strokeWidth="6"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 28}`}
                strokeDashoffset={`${2 * Math.PI * 28 * (1 - task?.progress / 100)}`}
                strokeLinecap="round"
                className="transition-all duration-500"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-sm font-bold text-foreground">{task?.progress}%</span>
            </div>
          </div>

          {/* Task Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4 mb-2">
              <h3 className="text-lg md:text-xl font-semibold text-foreground">
                {task?.title}
              </h3>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsExpanded(!isExpanded)}
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                >
                  <Icon name={isExpanded ? 'chevron-up' : 'chevron-down'} size={20} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowDeleteConfirm(true)}
                  aria-label="Delete task"
                >
                  <Icon name="trash-2" size={18} className="text-destructive" />
                </Button>
              </div>
            </div>

            <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
              {task?.description}
            </p>

            {/* Task Metadata */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                style={{ backgroundColor: `${getPriorityColor(task?.priority)}15`, color: getPriorityColor(task?.priority) }}
              >
                <Icon name={getPriorityIcon(task?.priority)} size={14} />
                <span className="capitalize">{task?.priority}</span>
              </div>

              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${
                isOverdue ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground'
              }`}>
                <Icon name="calendar" size={14} />
                <span>{format(new Date(task?.dueDate), 'MMM d, yyyy')}</span>
                {isOverdue && <span className="ml-1">(Overdue)</span>}
              </div>

              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-muted text-muted-foreground">
                <Icon name="users" size={14} />
                <span>{assignedMembersData?.length} members</span>
              </div>

              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-muted text-muted-foreground">
                <Icon name="message-circle" size={14} />
                <span>{task?.chatMessages?.length} messages</span>
              </div>
            </div>

            {/* Member Progress Bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">Team Progress</span>
                <span className="text-sm text-muted-foreground">
                  {completedCount} / {assignedMembersData?.length} completed
                </span>
              </div>
              <div className="relative h-3 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${(completedCount / (assignedMembersData?.length || 1)) * 100}%`,
                    background: generateMemberGradient()
                  }}
                />
                {assignedMembersData?.length === familyMembers?.length && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xs">✨</span>
                  </div>
                )}
              </div>
            </div>

            {/* Assigned Members Preview */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">Team:</span>
              <div className="flex -space-x-2">
                {assignedMembersData?.map(member => (
                  <div
                    key={member?.id}
                    className="w-8 h-8 rounded-full border-2 border-card flex items-center justify-center text-white text-xs font-semibold"
                    style={{ backgroundColor: member?.color }}
                    title={member?.name}
                  >
                    {member?.initials}
                  </div>
                ))}
              </div>
              {assignedMembersData?.length === familyMembers?.length && (
                <span className="text-sm ml-2">✨ Everyone</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-border">
          {/* Individual Member Completion Status */}
          <div className="p-4 md:p-6 bg-muted/30">
            <h4 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="check-square" size={18} />
              Individual Completion Status
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {assignedMembersData?.map(member => {
                const status = localCompletionStatus?.[member?.id];
                return (
                  <div
                    key={member?.id}
                    className="bg-card border border-border rounded-lg p-4 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold"
                        style={{ backgroundColor: member?.color }}
                      >
                        {member?.initials}
                      </div>
                      <div>
                        <div className="font-medium text-foreground">{member?.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {status?.completed ? (
                            <span className="text-success">
                              Completed {format(new Date(status?.completedAt), 'MMM d, h:mm a')}
                            </span>
                          ) : (
                            <span>Pending</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => onToggleMemberCompletion(task?.id, member?.id)}
                      className="w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-smooth"
                      style={{
                        borderColor: member?.color,
                        backgroundColor: status?.completed ? member?.color : 'transparent'
                      }}
                      aria-label={status?.completed ? 'Mark as incomplete' : 'Mark as complete'}
                    >
                      {status?.completed && (
                        <Icon name="check" size={14} color="#FFFFFF" />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Chat Section */}
          <div className="border-t border-border">
            <button
              onClick={() => setShowChat(!showChat)}
              className="w-full p-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Icon name="message-square" size={18} className="text-primary" />
                <span className="font-semibold text-foreground">Team Chat</span>
                <span className="text-sm text-muted-foreground">({task?.chatMessages?.length})</span>
              </div>
              <Icon name={showChat ? 'chevron-up' : 'chevron-down'} size={20} />
            </button>
            {showChat && (
              <TaskChat
                taskId={task?.id}
                messages={task?.chatMessages}
                familyMembers={familyMembers}
                onSendMessage={(message) => onSendMessage(task?.id, message)}
              />
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                <Icon name="alert-triangle" size={24} className="text-destructive" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Delete Task?</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                fullWidth
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                fullWidth
                onClick={handleDelete}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiMemberTaskCard;